import { CommonModule } from '@angular/common';
import { Component, inject, computed } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatButton, MatIconButton } from '@angular/material/button';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { EmailForwardingInfoComponent } from './email-forwarding-info/email-forwarding-info.component';
import { MatDialog } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatTooltip } from '@angular/material/tooltip';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { InboxService, COMPANY_NAME_TOKEN, ACCOUNT_GROUP_ID_TOKEN } from '../../../../core/src';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { GlobalParticipantType, EmailApiService } from '@vendasta/conversation';
import { map, switchMap, catchError, shareReplay, of, Observable } from 'rxjs';
@Component({
  standalone: true,
  selector: 'inbox-email-settings-card',
  imports: [
    CommonModule,
    MatIconModule,
    MatCardModule,
    TranslateModule,
    GalaxyPopoverModule,
    CdkOverlayOrigin,
    MatButton,
    MatIconButton,
    GalaxyFormFieldModule,
    MatTooltip,
    GalaxyTooltipModule,
    FormsModule,
  ],
  templateUrl: './email-settings-card.component.html',
  styleUrl: './email-settings-card.component.scss',
})
export class EmailSettingsCardComponent {
  private readonly emailApiService = inject(EmailApiService);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly user = toSignal(inject(InboxService).iamUser$);
  private readonly companyName = toSignal(inject(COMPANY_NAME_TOKEN));
  readonly forwardingAddress = toSignal(this.getForwardingAddress());
  constructor(
    private dialog: MatDialog,
    private clipboard: Clipboard,
  ) {}

  protected showPopover = false;
  protected isCopied = false;
  forwardingEmailTitle = computed(() => {
    const user = this.user();
    const companyName = this.companyName();
    return {
      firstName: user?.firstName,
      companyName: companyName,
      translationKey: user?.firstName
        ? 'INBOX.SETTINGS.EMAIL_SETTINGS.USER_AT_COMPANY'
        : 'INBOX.SETTINGS.EMAIL_SETTINGS.COMPANY_ONLY',
    };
  });

  openLearnMoreModal(): void {
    this.dialog.open(EmailForwardingInfoComponent, { width: '600px' });
  }

  copyClicked() {
    this.isCopied = true;
    this.clipboard.copy(this.forwardingAddress());
    setTimeout(() => {
      this.isCopied = false;
    }, 5000);
  }

  getForwardingAddress(): Observable<string> {
    return this.accountGroupId$.pipe(
      switchMap((accountGroupId) => {
        return this.emailApiService
          .getOwnerEmail({
            subjectParticipant: {
              participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
              internalParticipantId: accountGroupId,
            },
          })
          .pipe(catchError(() => of({ forwardingEmail: '' })));
      }),
      map((emailRes) => {
        return emailRes.forwardingEmail ?? '';
      }),
      catchError(() => of('')),
      shareReplay(1),
    );
  }
}
